import { refreshTokenApi } from "@/api/services/admin/auth";
import { LoadingScreen } from "@/components/elements/Loading/Loading";
import { AdminRoutes, AppRoutes } from "@/routes/config";
import { clientApi, isAuth, setAxiosAuth } from "@/utils/api";
import { ADMIN_EMAIL_KEY, ADMIN_USER_ID_KEY } from "@/utils/constants";
import { setLastAuthRoute } from "@/utils/lastAuthRoute";
import { logout } from "@/utils/logout";
import { createUrlWithParams } from "@/utils/url";
import Cookies from "js-cookie";
import { FC, useEffect, useRef, useState } from "react";
import { useLocation, useNavigate } from "react-router-dom";

export const withAuth: <CP extends {}>(WrappedComponent: FC<CP>) => FC<CP> = (WrappedComponent) => (props) => {
  const { pathname, search } = useLocation();
  const [isVerified, setIsVerified] = useState(false);
  const userIdRef = useRef(Cookies.get(ADMIN_USER_ID_KEY));
  const navigate = useNavigate();

  useEffect(() => {
    if (isAuth()) {
      setLastAuthRoute(`${pathname}${search}`);
    }
  }, [pathname, search]);

  useEffect(() => {
    // Every 2 seconds check user id cookie for account changes
    const intervalId = setInterval(() => {
      const currentUserId = Cookies.get(ADMIN_USER_ID_KEY);
      if (!userIdRef.current) return;
      if (!currentUserId) {
        const oldUrl = `${location.pathname}${location.search}`;
        const loginUrl = createUrlWithParams(AppRoutes.login, { redirectUrl: oldUrl });
        location.href = loginUrl;
      } else if (currentUserId !== userIdRef.current) {
        location.href = "/";
      }
    }, 2000);

    return () => {
      clearInterval(intervalId);
    };
  }, []);

  useEffect(() => {
    const verifyAuth = async () => {
      // set axios auth at init
      if (isAuth() && !clientApi.defaults.headers.common.Authorization) {
        setAxiosAuth();
      }
      // verify admin routes
      if (!isAuth() && AdminRoutes.some((path) => pathname.startsWith(path))) {
        const email = Cookies.get(ADMIN_EMAIL_KEY);
        const refreshToken = Cookies.get("admin-refresh-token");

        if (email && refreshToken) {
          try {
            await refreshTokenApi();
            setAxiosAuth();
            // Sync userId ref after successful refresh (account may have changed via center auth)
            userIdRef.current = Cookies.get(ADMIN_USER_ID_KEY);
            setIsVerified(true);
            return;
          } catch {
            logout();
          }
        }

        // Tokens are missing or refresh failed → clear the invalid session so
        // the next mount doesn't re-attempt a doomed refresh.
        logout();
        const oldUrl = `${location.pathname}${location.search}`;
        const loginUrl = createUrlWithParams(AppRoutes.login, { redirectUrl: oldUrl });
        location.href = loginUrl;
        return;
      }

      // Sync userId ref (account may have changed via center auth)
      userIdRef.current = Cookies.get(ADMIN_USER_ID_KEY);
      setIsVerified(true);
    };

    verifyAuth();
  }, [pathname, navigate]);

  if (!isVerified) return <LoadingScreen />;

  return <WrappedComponent {...props} />;
};
